import { useState, useCallback } from 'react';
import { message } from 'antd';
import { p9209Api, F90N } from '../../../services/p9209Api';
import { useAuth } from '../../../contexts/AuthContext';
import { useFieldErrors } from '../../../hooks/useFieldErrors';
import { Mode } from './useP9209Mode';

export function useP9209Record() {
  const { user } = useAuth();
  const [record, setRecord] = useState<F90N | null>(null);
  const [formData, setFormData] = useState<Partial<F90N>>({});
  const [loading, setLoading] = useState(false);
  const { setFieldErrors, clearFieldErrors, getFieldError, hasErrors } = useFieldErrors();

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: string): Promise<{ data: F90N } | null> => {
    setLoading(true);
    try {
      const data = await p9209Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      return { data };
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof F90N, value: F90N[keyof F90N]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof F90N): F90N[keyof F90N] => {
    return (formData as Record<string, F90N[keyof F90N]>)[key] ?? '';
  }, [formData]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: F90N) => void,
  ) => {
    setLoading(true);
    clearFieldErrors();
    try {
      const req = { master: formData as F90N };
      if (mode === 'add') {
        const result = await p9209Api.create(req);
        message.success(`新增成功: ${result.f90n_01}`);
        onSuccess(result);
      } else {
        const result = await p9209Api.update(formData.f90n_01!, req);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        const summary = errors.slice(0, 5).map(er => er.message).join('\n');
        const extra = errors.length > 5 ? `\n...共 ${errors.length} 項` : '';
        message.error({ content: summary + extra, duration: 6, style: { whiteSpace: 'pre-line' } });
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, setFieldErrors, clearFieldErrors]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await p9209Api.delete(record.f90n_01);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    clearFieldErrors();
    if (record) {
      setFormData({ ...record });
    }
  }, [record, clearFieldErrors]);

  /** Reset form for a new add */
  const resetForAdd = useCallback(() => {
    clearFieldErrors();
    setRecord(null);
    const today = new Date().toISOString().substring(0, 10) + 'T00:00:00Z';
    setFormData({
      f90n_11: user?.userId || '',
      f90n_12: today,
      f90n_80: 'V',
      f90n_91: user?.userId || '',
    });
  }, [user, clearFieldErrors]);

  /** Apply result after save */
  const applyResult = useCallback((result: F90N) => {
    setRecord(result);
    setFormData({ ...result });
  }, []);

  return {
    record, formData, loading,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    getFieldError, hasErrors,
  };
}
